import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { InvoiceExtractionData } from './invoice-field-mapper';

export const INVOICE_MAX_BYTES = 10 * 1024 * 1024; // same client limit as the Smart Form Filler
/** Only the AI engines are invoice-aware; the policy roles run a rule-based policy parser. */
export const INVOICE_EXTRACTION_ROLES = ['pdfUploadOcrAI', 'pdfUploadOcrClaude'];

/**
 * Thin client for the EXISTING backend endpoint POST /pdf/extract-policy.
 * Mirrors the request contract used by the Smart Form Filler (multipart field "pdf", 30s timeout).
 */
@Injectable({ providedIn: 'root' })
export class InvoiceExtractionService {
  private readonly endpoint = `${environment.apiUrl}/pdf/extract-policy`;

  constructor(private http: HttpClient) {}

  hasAccess(specialRoleAccess: string[] | undefined): boolean {
    return !!specialRoleAccess && INVOICE_EXTRACTION_ROLES.some((r) => specialRoleAccess.includes(r));
  }

  /** Returns an error message for unusable files, otherwise null. */
  validate(file: File): string | null {
    if (!file) return 'No file selected.';
    if (file.type !== 'application/pdf') return 'Auto-fill supports PDF invoices only.';
    if (file.size === 0) return 'The selected file is empty.';
    if (file.size > INVOICE_MAX_BYTES) return 'Invoice is larger than 10MB; auto-fill skipped.';
    return null;
  }

  extract(file: File): Observable<InvoiceExtractionData> {
    const formData = new FormData();
    formData.append('pdf', file);
    return this.http.post<{ success: boolean; data?: InvoiceExtractionData; error?: string }>(this.endpoint, formData).pipe(
      timeout(30000),
      map((res) => {
        if (!res || !res.success) throw new Error((res && res.error) || 'Extraction failed.');
        return res.data || {};
      }),
      catchError((err) => throwError(() => new Error(this.message(err))))
    );
  }

  private message(err: any): string {
    if (err && err.name === 'TimeoutError') return 'Extraction timed out.';
    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) return 'Network error while reading the invoice.';
      if (err.status === 403) return 'You do not have access to invoice auto-fill.';
      if (err.status === 413) return 'Invoice file is too large.';
      return err.error?.error || `Could not read the invoice (${err.status}).`;
    }
    return err?.message || 'Could not read the invoice.';
  }
}
